import FormLayout from "src/layouts/FormLayout";
import { useWebClientApi } from "src/api";
import {
  fromEthnicities,
  toEthnicitiesUpdateReq,
  type EthnicityFormData,
} from "src/api/adapters";
import { useResponseRedirect } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { Col } from "src/components/layout";
import type { DtoGetDemographicsResp } from "@porchpass/api-client";
import {
  hasEthnicity,
  isEthnicityOtherSectionValid,
  validate,
  test,
} from "src/utils";
import {
  EcoaEthnicityFields,
  FormTable,
  FormTableBody,
  UserIdVerificationIcon,
} from "@porchpass/ui";
import { useMemo } from "react";
import {
  fromEcoaEthnicityFormData,
  toEcoaEthnicityFormData,
} from "src/forms/adapters/ecoa";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import Disclaimer from "./EthnicityDisclaimer";

const validationMapper = () => ({
  ethnicities: [
    test(hasEthnicity, "Must select at least one option"),
    test(
      isEthnicityOtherSectionValid,
      "Other Hispanic or Latino must have a value",
    ),
  ],
});

export default () => {
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<EthnicityFormData>(
    {},
    () => api.getDemographics(),
    (data) => fromEthnicities(data as DtoGetDemographicsResp),
  );
  const [submitHandler, loading, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: EthnicityFormData) =>
      api.updateDemographics(toEthnicitiesUpdateReq(data)),
    );
  useResponseRedirect("/ethnicity/race", response);
  const formErrors = validate(formData as never, validationMapper as never) as {
    ethnicities?: { message: string };
  };

  const ethnicityValue = useMemo(
    () =>
      toEcoaEthnicityFormData(
        formData as Parameters<typeof toEcoaEthnicityFormData>[0],
      ),
    [formData],
  );

  const visibleErrors = fieldErrorsAfterSubmit(
    { ethnicities: formErrors.ethnicities?.message },
    hasAttemptedFormSubmission,
  );

  return (
    <FormLayout
      title="Do you identify as hispanic or latino?"
      headerTitle="Ethnic Background"
      stepIndex={9}
      footerTitle="Next"
      clickHandler={() => void submitHandler(formData, formErrors as never)}
      isLoading={loading}
    >
      <Col>
        <FormTable
          title="Equal Credit Opportunity Act"
          headerIcon={
            <UserIdVerificationIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#CA7900]"
            />
          }
        >
          <FormTableBody>
            <EcoaEthnicityFields
              value={ethnicityValue}
              onChange={(patch) => {
                const payload = fromEcoaEthnicityFormData({
                  ...ethnicityValue,
                  ...patch,
                });
                updateField("ethnicities")(payload.ethnicities ?? []);
              }}
              errors={visibleErrors}
            />
          </FormTableBody>
        </FormTable>
        <Disclaimer />
      </Col>
    </FormLayout>
  );
};
